/**
 * Estilos del lanzador y del contenedor del panel.
 *
 * Van en una hoja construida por CSSOM y no en un `<style>` inyectado por la CSP del cliente: un
 * `<style>` en el DOM, aunque sea dentro de un shadow root, está sujeto a `style-src`, y una
 * política estricta dejaría el widget sin estilos. Todo vive en el shadow root, así que los
 * selectores pueden ser genéricos.
 */

export const LAUNCHER_STYLES = `
:host {
  --atlas-primary: #0f766e;
  --atlas-on-primary: #ffffff;
  --atlas-radius: 16px;

  /* Casi el máximo entero de 32 bits: por encima de lo razonable del cliente, sin ocupar el tope
     para dejar margen a un aviso de cookies o similar. */
  all: initial;
  position: fixed;
  z-index: 2147483000;
  inset: auto 0 0 auto;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
}

.launcher {
  position: fixed;
  right: 20px;
  bottom: 20px;
  width: 56px;
  height: 56px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: var(--atlas-primary);
  color: var(--atlas-on-primary);
  cursor: pointer;
  box-shadow: 0 6px 20px rgb(0 0 0 / 0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 160ms ease, box-shadow 160ms ease;
}

.launcher:hover { transform: scale(1.05); }
.launcher:active { transform: scale(0.97); }

/* El foco no puede depender del color de marca: el cliente elige uno cualquiera y el indicador
   tiene que verse sobre todos (A11Y-07). */
.launcher:focus-visible {
  outline: 3px solid #ffffff;
  outline-offset: -6px;
  box-shadow: 0 0 0 3px var(--atlas-primary), 0 6px 20px rgb(0 0 0 / 0.22);
}

.launcher svg { width: 26px; height: 26px; display: block; }

/* Con avatar, la foto ocupa la burbuja y el color de marca queda como un anillo fino alrededor. */
.launcher.has-avatar { padding: 2px; }
.launcher.has-avatar svg { display: none; }

.avatar {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}

/* Con el panel abierto el lanzador desaparece: el panel ya trae su botón de cerrar. */
.launcher.is-hidden { display: none; }

.frame {
  position: fixed;
  right: 20px;
  bottom: 88px;
  width: 400px;
  height: min(620px, calc(100vh - 128px));
  border: 0;
  border-radius: var(--atlas-radius);
  background: #ffffff;
  box-shadow: 0 12px 40px rgb(0 0 0 / 0.24);
  opacity: 0;
  transform: translateY(12px);
  pointer-events: none;
  transition: opacity 160ms ease, transform 160ms ease;
}

.frame.is-open {
  opacity: 1;
  transform: none;
  pointer-events: auto;
}

/* En pantallas pequeñas el panel ocupa todo: una tarjeta de 400 px con márgenes no cabe. */
@media (max-width: 480px), (max-height: 560px) {
  .frame {
    inset: 0;
    width: 100%;
    height: 100%;
    border-radius: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .launcher, .frame { transition: none; }
  .frame { transform: none; }
}
`;
